import { useState, type ReactElement } from 'react';
import { Button } from '@vwpfs/mary-air-lib/components/air/05-atoms/Button';
import { ThemePalette } from '@vwpfs/mary-air-lib/theme/_Types';
import { can, toUserRoles } from '../auth/roles';
import { useAuth } from '../auth/useAuth';
import { useReserveVehicle, useSetVehicleUseForAd } from '../hooks/useVehicleMutations';
import { ReasonModal } from './ReasonModal';
import { AdvertisingModal } from './AdvertisingModal';
import { OverrideHistoryModal } from './OverrideHistoryModal';
import type { UseForAdSetting, VehicleListItem } from '../types/vehicle';

interface Props {
  vehicle: VehicleListItem;
}

type ActiveModal = 'reserve' | 'advertising' | 'history' | null;

function vehicleLabel(vehicle: VehicleListItem): string {
  const plate = vehicle.licensePlate ?? '—';
  const description = [vehicle.brand, vehicle.model].filter(Boolean).join(' ');
  return description ? `${plate} (${description})` : plate;
}

export function VehicleActions({ vehicle }: Props): ReactElement {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const { user } = useAuth();

  const reserveMutation = useReserveVehicle();
  const useForAdMutation = useSetVehicleUseForAd();
  const userRoles = toUserRoles(user?.roles ?? []);
  const canReserve = can(userRoles, 'reserve');
  const canAdvertise = can(userRoles, 'advertising');

  const close = (): void => setActiveModal(null);

  const onReserveConfirm = (reason: string): void => {
    reserveMutation.mutate(
      { vehicleId: vehicle.id, reserved: !vehicle.mobilistStatusReserved, reason: reason || undefined },
      { onSuccess: close }
    );
  };

  const onUseForAdConfirm = (settings: UseForAdSetting[], reason: string): void => {
    useForAdMutation.mutate({ vehicleId: vehicle.id, settings, reason: reason || undefined }, { onSuccess: close });
  };

  const reserveLabel = vehicle.mobilistStatusReserved ? 'Unreserve' : 'Reserve';

  return (
    <div className="vap-vehicle-actions">
      <Button
        buttonType="button"
        theme={{ palette: ThemePalette.CONTRAST_SECONDARY }}
        link={{ onClick: () => setActiveModal('reserve') }}
        disabled={!canReserve}
      >
        {reserveLabel}
      </Button>
      <Button
        buttonType="button"
        theme={{ palette: ThemePalette.CONTRAST_SECONDARY }}
        link={{ onClick: () => setActiveModal('advertising') }}
        disabled={!canAdvertise}
      >
        Advertising
      </Button>
      {/* Reading the trail is not an override, so it is open to anyone who can reach the portal. */}
      <Button
        buttonType="button"
        theme={{ palette: ThemePalette.CONTRAST_SECONDARY }}
        link={{ onClick: () => setActiveModal('history') }}
      >
        History
      </Button>

      <ReasonModal
        title={`${reserveLabel} vehicle`}
        description={`${reserveLabel} ${vehicleLabel(vehicle)}? Vehicle stays visible on the consumer frontend, but lead requests are blocked while reserved.`}
        isOpen={activeModal === 'reserve'}
        isSubmitting={reserveMutation.isPending}
        onCancel={close}
        onConfirm={onReserveConfirm}
        confirmLabel={reserveLabel}
      />

      {activeModal === 'history' && (
        <OverrideHistoryModal vehicleId={vehicle.id} vehicleLabel={vehicleLabel(vehicle)} onClose={close} />
      )}

      <AdvertisingModal
        vehicleLabel={vehicleLabel(vehicle)}
        dealerLocationId={vehicle.dealerLocationId}
        currentSettings={vehicle.mobilistUseForAd}
        isOpen={activeModal === 'advertising'}
        isSubmitting={useForAdMutation.isPending}
        onCancel={close}
        onConfirm={onUseForAdConfirm}
      />
    </div>
  );
}
